import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DonkeyGuide } from "@/components/DonkeyMascot";
import { toast } from "sonner";
import { Loader2, FlaskConical, ArrowLeft, Trophy, History, Trash2, Scale } from "lucide-react";

const EV_TYPES = [
  ["deposit", "Deposit / pre-payment taken"], ["purchase", "Purchase / paid pilot"],
  ["binding_commitment", "Signed / binding commitment"], ["price_agreed", "Verbally agreed to pay (unpaid)"],
  ["landing_conversion", "Landing page: sign-up / checkout"], ["landing_visit", "Landing page: visits only"],
  ["customer_conversation", "Customer conversation"], ["positive_response", "Positive response"],
  ["negative_response", "Negative response"], ["rejection", "Rejection (with reason)"],
  ["like_or_compliment", "Like / compliment"], ["survey_enthusiasm", "Survey enthusiasm"],
  ["friend_opinion", "Friend / family opinion"], ["upload", "Uploaded supporting evidence"],
];
const DECISION_TONE = {
  proceed: "text-donkey-green border-donkey-green/50 bg-donkey-green/10",
  pivot: "text-donkey-amber border-donkey-amber/50 bg-donkey-amber/10",
  stop: "text-donkey-red border-donkey-red/50 bg-donkey-red/10",
  more_evidence: "text-donkey-orange border-donkey-orange/50 bg-donkey-orange/10",
};
const strengthTone = (s) => s === "real_commitment" ? "text-donkey-green border-donkey-green/40"
  : s === "weak_signal" ? "text-donkey-amber border-donkey-amber/40"
  : s === "negative_signal" ? "text-donkey-red border-donkey-red/40" : "text-donkey-grey border-white/20";

export default function Validation() {
  const { reportId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [sel, setSel] = useState(null);         // selected experiment
  const [evidence, setEvidence] = useState([]);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [oppIdx, setOppIdx] = useState(Number(sp.get("opp") || 0));
  const [form, setForm] = useState({ type: "deposit", content: "", amount: "", file: null });

  const load = useCallback(async () => {
    try {
      const [r, ex, de] = await Promise.all([
        api.get(`/reports/${reportId}`),
        api.get(`/reports/${reportId}/experiments`),
        api.get(`/reports/${reportId}/decisions`),
      ]);
      setReport(r.data); setExperiments(ex.data.experiments); setDecisions(de.data.decisions);
    } catch (e) { toast.error("Could not load validation data"); }
    finally { setLoading(false); }
  }, [reportId]);
  useEffect(() => { load(); }, [load]);

  const openExp = async (e) => {
    setSel(e); setDecision(null);
    const { data } = await api.get(`/experiments/${e.id}/evidence`);
    setEvidence(data.evidence);
    const d = decisions.find((x) => x.experiment_id === e.id);
    if (d) setDecision(d);
  };

  const generatePlan = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("opportunity_index", String(oppIdx));
      await api.post(`/reports/${reportId}/validation/plan`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load(); toast.success("First Evidence Plan created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate plan"); }
    finally { setBusy(false); }
  };

  const addEvidence = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("type", form.type); fd.append("content", form.content);
      if (form.amount) fd.append("amount", form.amount);
      if (form.file) fd.append("file", form.file);
      await api.post(`/experiments/${sel.id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/experiments/${sel.id}/evidence`);
      setEvidence(data.evidence); setForm({ type: "deposit", content: "", amount: "", file: null });
      toast.success("Evidence recorded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not record evidence"); }
    finally { setBusy(false); }
  };

  const getDecision = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/experiments/${sel.id}/decision`);
      setDecision(data); await load(); toast.success("Decision recorded");
    } catch (e) { toast.error("Could not reach a decision"); }
    finally { setBusy(false); }
  };

  const opps = report?.analysis?.opportunities || [];

  if (loading) return <div className="min-h-screen bg-donkey-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-donkey-orange" /></div>;

  return (
    <div className="min-h-screen bg-donkey-bg text-donkey-warm">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-8">
        <button onClick={() => navigate(`/report/${reportId}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5 mb-6" data-testid="back-to-report-btn">
          <ArrowLeft className="w-4 h-4" /> Back to report
        </button>
        <div className="flex items-center gap-2 mb-1"><FlaskConical className="w-6 h-6 text-donkey-orange" /><h1 className="font-heading font-extrabold text-3xl">Prove it — Validation</h1></div>
        <p className="text-donkey-grey text-sm mb-6">Get real evidence before you spend real money. Decisions here are based on recorded evidence, not optimism.</p>

        {!sel ? (
          <>
            {/* Dashboard */}
            <DonkeyGuide title="Where the Donkey stands" className="mb-6">
              Pick the opportunity you want to test. I'll find the single most important unproven assumption and turn it
              into cheap experiments with hard success and failure bars — never above your minimum loss limit.
            </DonkeyGuide>

            {experiments.length === 0 && (
              <div className="card-donkey rounded-2xl p-6 mb-8" data-testid="plan-generator">
                <h2 className="font-heading font-semibold text-lg mb-3">Create your First Evidence Plan</h2>
                <label className="text-xs text-donkey-grey">Opportunity</label>
                <select value={oppIdx} onChange={(e) => setOppIdx(Number(e.target.value))} data-testid="opp-select"
                  className="mt-1 mb-4 block w-full bg-donkey-bg border border-donkey-line rounded-lg px-3 py-2 text-sm">
                  {opps.map((o, i) => <option key={i} value={i}>{o.rank_label ? `${o.rank_label}: ` : ""}{o.name}</option>)}
                </select>
                <Button onClick={generatePlan} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="generate-plan-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Generate First Evidence Plan</>}
                </Button>
              </div>
            )}

            {experiments.length > 0 && (
              <div className="space-y-3 mb-10" data-testid="experiment-list">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading font-semibold text-lg">Experiments</h2>
                  <Button size="sm" variant="ghost" onClick={generatePlan} disabled={busy} className="text-donkey-grey border border-donkey-line">+ New plan</Button>
                </div>
                {experiments.map((e) => (
                  <button key={e.id} onClick={() => openExp(e)} data-testid={`experiment-card-${e.id}`}
                    className="w-full text-left card-donkey rounded-xl p-4 hover:border-donkey-orange/50 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm">{e.question}</p>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${e.status === "complete" ? "text-donkey-green border-donkey-green/40" : "text-donkey-grey border-white/20"}`}>{e.status}</span>
                    </div>
                    <p className="text-xs text-donkey-grey mt-1">{e.opportunity_name} · target: {e.target_customer} · ≤£{e.max_spend} · by {e.deadline}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Decision history */}
            {decisions.length > 0 && (
              <div data-testid="decision-history">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2 mb-3"><History className="w-4 h-4 text-donkey-orange" /> Decision history</h2>
                <div className="space-y-2">
                  {decisions.map((d) => (
                    <div key={d.id} className="card-donkey rounded-xl p-3 flex items-center justify-between gap-3">
                      <div><span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${DECISION_TONE[d.decision]}`}>{(d.decision || "").replace("_", " ")}</span>
                        <span className="text-xs text-donkey-grey ml-2">{d.opportunity_name} · evidence {d.evidence_quality} · {String(d.created_at).slice(0, 10)}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Experiment detail */}
            <button onClick={() => { setSel(null); setDecision(null); }} className="text-sm text-donkey-grey hover:text-donkey-orange mb-4" data-testid="back-to-dashboard-btn">← All experiments</button>
            <div className="card-donkey rounded-2xl p-6 mb-6" data-testid="experiment-detail">
              <p className="text-xs uppercase tracking-wide text-donkey-orange mb-1">Testing assumption</p>
              <p className="text-sm text-donkey-warm/90 mb-3">{sel.assumption}</p>
              <h2 className="font-heading font-semibold text-lg">{sel.question}</h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
                <div><span className="text-donkey-grey text-xs">Hypothesis</span><p>{sel.hypothesis}</p></div>
                <div><span className="text-donkey-grey text-xs">Target customer</span><p>{sel.target_customer}</p></div>
                <div><span className="text-donkey-grey text-xs">Action</span><p>{sel.action}</p></div>
                <div><span className="text-donkey-grey text-xs">Evidence required</span><p>{sel.evidence_required}</p></div>
                <div><span className="text-donkey-green text-xs">Success</span><p>{sel.success_criteria}</p></div>
                <div><span className="text-donkey-red text-xs">Failure</span><p>{sel.failure_criteria}</p></div>
                <div><span className="text-donkey-grey text-xs">Max spend</span><p className="font-mono">£{sel.max_spend}</p></div>
                <div><span className="text-donkey-grey text-xs">Deadline</span><p>{sel.deadline} ({sel.max_time})</p></div>
              </div>
            </div>

            {/* Evidence recording */}
            <div className="card-donkey rounded-2xl p-6 mb-6" data-testid="evidence-recorder">
              <h3 className="font-heading font-semibold mb-3">Record evidence</h3>
              <p className="text-xs text-donkey-grey mb-3">Likes, compliments, survey enthusiasm and friends' opinions are logged honestly but do <span className="text-donkey-amber">not</span> count as reliable demand.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="evidence-type-select"
                  className="bg-donkey-bg border border-donkey-line rounded-lg px-3 py-2 text-sm">
                  {EV_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount £ (if paid)" className="bg-donkey-bg border-donkey-line" data-testid="evidence-amount-input" />
                <input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt,.docx" className="text-xs text-donkey-grey file:mr-2 file:rounded file:border-0 file:bg-donkey-orange file:text-donkey-black file:px-3 file:py-1.5 file:font-semibold" data-testid="evidence-file-input" />
              </div>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={2} placeholder="What happened? (who, what they said/did, price, reason for rejection…)" className="mt-3 bg-donkey-bg border-donkey-line" data-testid="evidence-content-input" />
              <Button onClick={addEvidence} disabled={busy} className="mt-3 bg-donkey-orange text-donkey-black font-semibold" data-testid="add-evidence-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add evidence"}</Button>
            </div>

            {/* Results */}
            <div className="card-donkey rounded-2xl p-6 mb-6" data-testid="experiment-results">
              <h3 className="font-heading font-semibold mb-3">Recorded evidence ({evidence.length})</h3>
              {evidence.length === 0 ? <p className="text-sm text-donkey-grey italic">None yet.</p> : (
                <ul className="space-y-2">
                  {evidence.map((r) => (
                    <li key={r.id} className="rounded-lg bg-donkey-bg border border-donkey-line p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase rounded border px-1.5 py-0.5 ${strengthTone(r.strength)}`} data-testid="evidence-strength">{r.strength_label}</span>
                        <span className="text-xs text-donkey-warm/90">{r.type_label}</span>
                        {r.amount ? <span className="text-xs font-mono text-donkey-green">£{r.amount}</span> : null}
                      </div>
                      {r.content && <p className="text-sm text-donkey-warm/80 mt-1">{r.content}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Decision */}
            <div className="card-donkey rounded-2xl p-6" data-testid="decision-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold flex items-center gap-2"><Scale className="w-4 h-4 text-donkey-orange" /> Proceed / Pivot / Stop</h3>
                <Button onClick={getDecision} disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="get-decision-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Weigh the evidence"}</Button>
              </div>
              {decision && (
                <div data-testid="decision-result">
                  <span className={`inline-block text-sm font-bold uppercase px-3 py-1 rounded-lg border ${DECISION_TONE[decision.decision]}`}>{(decision.decision || "").replace("_", " ")}</span>
                  <p className="text-xs text-donkey-grey mt-2">Evidence quality: <span className="text-donkey-warm">{decision.evidence_quality}</span> · reliable-demand signals: {decision.reliable_demand_count}/{decision.evidence_count}</p>
                  <div className="mt-3 space-y-2 text-sm">
                    <p><span className="text-donkey-grey text-xs">Against your criteria: </span>{decision.criteria_comparison}</p>
                    <p><span className="text-donkey-grey text-xs">Why: </span>{decision.rationale}</p>
                    <p><span className="text-donkey-amber text-xs">Still uncertain: </span>{decision.remains_uncertain}</p>
                    <p><span className="text-donkey-orange text-xs">Do next: </span>{decision.next_steps}</p>
                  </div>
                  <p className="text-[11px] text-donkey-grey mt-3 italic">This updates the opportunity's validation evidence quality and decision history — your original Stage 1 verdict is preserved.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
